/**
 * ApprovePublishDialog  — Approve & Publish entry point
 *
 * Changes in this iteration
 * ─────────────────────────
 * • Badge: button badge now shows job count (253), not batch count (83)
 * • Filters: added Job-date from/to and Provenance-date from/to date pickers
 * • Provenance: "Submitted by / when" is truthful per batch state:
 *     - batch_status = 'pending_approval' / 'approved': shows submitted_by_name + submitted_at
 *     - batch_status = 'draft' (never submitted): shows batch_scheduler_name + "Created [batch_created_at]"
 *     Never invents timestamps.
 */

import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Loader2, Check, ChevronLeft,
  Search, X, ShieldCheck, Globe, Lock, XCircle,
} from 'lucide-react';
import { Textarea } from './ui/textarea';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const monthKey = d => (d ? d.slice(0, 7) : '');
const monthLabel = key => {
  if (!key) return '';
  const [y, m] = key.split('-');
  return new Date(+y, +m - 1, 1).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
};
const fmtDate = d => d ? format(parseISO(d), 'd MMM yyyy') : '—';
const fmtRange = j => {
  const s = fmtDate(j.scheduled_date);
  return (!j.end_date || j.end_date === j.scheduled_date) ? s : `${s} → ${fmtDate(j.end_date)}`;
};
const fmtRel = iso => {
  if (!iso) return null;
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return iso.slice(0, 10); }
};

/** Derive the truthful provenance for a job row. */
const provenance = job => {
  const submitted = job.submitted_at;
  if (submitted) {
    return {
      by:    job.submitted_by_name || '—',
      when:  fmtRel(submitted),
      whenIso: submitted.slice(0, 10),
      label: 'Submitted',
    };
  }
  // Draft: never submitted — show scheduler (creator) + batch created_at
  return {
    by:    job.batch_scheduler_name || '—',
    when:  job.batch_created_at ? fmtRel(job.batch_created_at) : null,
    whenIso: job.batch_created_at ? job.batch_created_at.slice(0, 10) : '',
    label: 'Draft (not submitted)',
    isDraft: true,
  };
};

/**
 * The name to show in the Requester column AND use for filtering.
 * For cross-batch proposal rows the proposal's creator (proposal_submitted_by)
 * is the real requester; for all other rows fall back to provenance().by.
 */
const requesterName = job =>
  job.proposal_submitted_by || provenance(job).by || '—';

function sortedJobs(jobs, key, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  return [...jobs].sort((a, b) => {
    let av, bv;
    switch (key) {
      case 'date':
        av = a.scheduled_date || ''; bv = b.scheduled_date || ''; break;
      case 'site':
        av = (a.site_name || '').toLowerCase(); bv = (b.site_name || '').toLowerCase(); break;
      case 'auditors':
        av = (a.auditor_names || []).join().toLowerCase();
        bv = (b.auditor_names || []).join().toLowerCase(); break;
      case 'by': {
        const pa = provenance(a); const pb = provenance(b);
        av = (pa.by || '').toLowerCase(); bv = (pb.by || '').toLowerCase(); break;
      }
      case 'when': {
        // Sort by ISO string of submission or creation time
        av = a.submitted_at || a.batch_created_at || '';
        bv = b.submitted_at || b.batch_created_at || '';
        break;
      }
      case 'status': av = a.approval_state; bv = b.approval_state; break;
      default: return 0;
    }
    return av < bv ? -1 * mul : av > bv ? 1 * mul : 0;
  });
}

function SortHeader({ label, col, sortKey, sortDir, onSort }) {
  const active = sortKey === col;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-900 select-none"
      onClick={() => onSort(col)}
    >
      {label}<Icon className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} />
    </button>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function ApprovePublishDialog({
  open,
  onOpenChange,
  onActionComplete,
}) {
  // ── data ──────────────────────────────────────────────────────────────────
  const [allJobs, setAllJobs]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ── per-job action sets ───────────────────────────────────────────────────
  const [approveIds, setApproveIds] = useState(new Set());
  const [publishIds, setPublishIds] = useState(new Set());
  // ── reject state ──────────────────────────────────────────────────────────
  const [rejectIds, setRejectIds]     = useState(new Set());
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejecting, setRejecting]     = useState(false);
  const [rejectError, setRejectError] = useState(''); // inline error for rejection modal

  // ── navigation ────────────────────────────────────────────────────────────
  const [selMonth, setSelMonth] = useState(null);

  // ── sort ──────────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  // ── filters ───────────────────────────────────────────────────────────────
  const [filterText, setFilterText]           = useState('');
  const [filterState, setFilterState]         = useState('all');
  const [filterBy, setFilterBy]               = useState('all');
  const [filterDateFrom, setFilterDateFrom]   = useState('');
  const [filterDateTo, setFilterDateTo]       = useState('');
  const [filterProvFrom, setFilterProvFrom]   = useState('');
  const [filterProvTo, setFilterProvTo]       = useState('');

  // ── step ──────────────────────────────────────────────────────────────────
  const [step, setStep]             = useState('browse');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Load on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep('browse');
    setApproveIds(new Set());
    setPublishIds(new Set());
    setRejectIds(new Set());
    setRejectReason('');
    setShowRejectModal(false);
    setRejectError('');
    setFilterText('');
    setFilterState('all');
    setFilterBy('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterProvFrom('');
    setFilterProvTo('');
    setSortKey('date');
    setSortDir('asc');
    setSubmitError(null);
    setLoadError(null);

    setLoading(true);
    api.get('/batch-schedule/jobs/pending-action')
      .then(r => {
        const jobs = r.data || [];
        setAllJobs(jobs);
        const keys = [...new Set(jobs.map(j => monthKey(j.scheduled_date)).filter(Boolean))].sort();
        setSelMonth(keys[0] || null);
      })
      .catch(e => setLoadError(e?.response?.data?.detail || 'Failed to load pending jobs.'))
      .finally(() => setLoading(false));
  }, [open]);

  // ── Month pills ───────────────────────────────────────────────────────────
  const monthGroups = useMemo(() => {
    const m = new Map();
    allJobs.forEach(j => {
      const k = monthKey(j.scheduled_date);
      if (!k) return;
      if (!m.has(k)) m.set(k, { key: k, label: monthLabel(k), needs: 0, publish: 0 });
      const g = m.get(k);
      if (j.approval_state === 'NEEDS_APPROVAL') g.needs++;
      else g.publish++;
    });
    return [...m.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [allJobs]);

  // ── Jobs for selected month ───────────────────────────────────────────────
  const monthJobs = useMemo(
    () => allJobs.filter(j => monthKey(j.scheduled_date) === selMonth),
    [allJobs, selMonth],
  );

  // ── Unique submitters/creators in this month ──────────────────────────────
  // Use requesterName() — consistent with the Requester column display —
  // so cross-batch proposal rows (whose batch is still DRAFT) show the actual
  // requester name rather than "—" from provenance().by.
  const submitters = useMemo(
    () => [...new Set(monthJobs.map(j => requesterName(j)).filter(n => n && n !== '—'))].sort(),
    [monthJobs],
  );

  // ── Filtered + sorted jobs ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = monthJobs;

    // State filter
    if (filterState !== 'all')
      list = list.filter(j =>
        filterState === 'needs_approval'
          ? j.approval_state === 'NEEDS_APPROVAL'
          : j.approval_state === 'APPROVED_UNPUBLISHED',
      );

    // Text search: site, auditor, batch
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      list = list.filter(j =>
        (j.site_name || '').toLowerCase().includes(q) ||
        (j.auditor_names || []).some(a => a.toLowerCase().includes(q)) ||
        (j.batch_name || '').toLowerCase().includes(q),
      );
    }

    // Submitter/requester filter — uses requesterName() to match the column display
    if (filterBy !== 'all')
      list = list.filter(j => requesterName(j) === filterBy);

    // Job date range
    if (filterDateFrom)
      list = list.filter(j => (j.scheduled_date || '') >= filterDateFrom);
    if (filterDateTo)
      list = list.filter(j => (j.scheduled_date || '') <= filterDateTo);

    // Provenance date range (submitted_at or batch_created_at)
    if (filterProvFrom)
      list = list.filter(j => {
        const t = provenance(j).whenIso;
        return t && t >= filterProvFrom;
      });
    if (filterProvTo)
      list = list.filter(j => {
        const t = provenance(j).whenIso;
        return t && t <= filterProvTo;
      });

    return sortedJobs(list, sortKey, sortDir);
  }, [
    monthJobs, filterState, filterText, filterBy,
    filterDateFrom, filterDateTo, filterProvFrom, filterProvTo,
    sortKey, sortDir,
  ]);

  const filteredIds = useMemo(() => filtered.map(j => j.id), [filtered]);

  // ── Sort toggle ───────────────────────────────────────────────────────────
  const handleSort = useCallback(col => {
    setSortKey(prev => {
      if (prev === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else setSortDir('asc');
      return col;
    });
  }, []);

  // ── Per-job toggle helpers ────────────────────────────────────────────────
  const toggleApprove = useCallback((job) => {
    if (job.approval_state !== 'NEEDS_APPROVAL') return;
    setApproveIds(prev => {
      const next = new Set(prev);
      if (next.has(job.id)) {
        next.delete(job.id);
        setPublishIds(p => { const q = new Set(p); q.delete(job.id); return q; });
      } else {
        next.add(job.id);
        // Approve clears any reject selection for same job
        setRejectIds(p => { const q = new Set(p); q.delete(job.id); return q; });
      }
      return next;
    });
  }, []);

  const togglePublish = useCallback((job) => {
    setPublishIds(prev => {
      const next = new Set(prev);
      if (next.has(job.id)) {
        next.delete(job.id);
      } else {
        next.add(job.id);
        if (job.approval_state === 'NEEDS_APPROVAL')
          setApproveIds(p => new Set([...p, job.id]));
      }
      return next;
    });
  }, []);

  const toggleReject = useCallback((job) => {
    if (job.approval_state !== 'NEEDS_APPROVAL') return;
    setRejectIds(prev => {
      const next = new Set(prev);
      if (next.has(job.id)) {
        next.delete(job.id);
      } else {
        next.add(job.id);
        // Reject clears approve/publish for same job
        setApproveIds(p => { const q = new Set(p); q.delete(job.id); return q; });
        setPublishIds(p => { const q = new Set(p); q.delete(job.id); return q; });
      }
      return next;
    });
  }, []);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setRejecting(true);
    setRejectError('');

    // Separate the two row types so the backend can route each independently.
    // is_cross_batch_proposal rows must go to proposal_job_ids so the backend
    // deletes the proposal without touching the original job's state.
    const regularJobIds   = [];
    const proposalJobIds  = [];
    for (const jid of rejectIds) {
      const job = allJobs.find(j => j.id === jid);
      if (job?.is_cross_batch_proposal) proposalJobIds.push(jid);
      else regularJobIds.push(jid);
    }

    // POST — rejection only; errors show inline (modal stays open)
    try {
      await api.post('/batch-schedule/jobs/reject', {
        job_ids: regularJobIds,
        proposal_job_ids: proposalJobIds,
        reason: rejectReason.trim(),
      });
    } catch (postErr) {
      setRejectError(postErr?.response?.data?.detail || 'Rejection failed. Please try again.');
      setRejecting(false);
      return;
    } finally {
      setRejecting(false);
    }

    // Rejection committed — close modal and reload queue
    setRejectIds(new Set());
    setRejectReason('');
    setShowRejectModal(false);
    setRejectError('');
    setLoading(true);
    try {
      const r = await api.get('/batch-schedule/jobs/pending-action');
      const jobs = r.data || [];
      setAllJobs(jobs);
      const keys = [...new Set(jobs.map(j => monthKey(j.scheduled_date)).filter(Boolean))].sort();
      setSelMonth(k => keys.includes(k) ? k : (keys[0] || null));
      if (onActionComplete) onActionComplete();
    } catch {
      // Reload failed but rejection was committed — user can close/reopen for fresh data
    } finally {
      setLoading(false);
    }
  }, [rejectIds, rejectReason, allJobs, onActionComplete]);

  // ── Select-all per column (filtered view) ────────────────────────────────
  const approveEligible = filteredIds.filter(id => allJobs.find(j => j.id === id)?.approval_state === 'NEEDS_APPROVAL');
  const allApproveChk  = approveEligible.length > 0 && approveEligible.every(id => approveIds.has(id));
  const someApproveChk = approveEligible.some(id => approveIds.has(id));
  const allPublishChk  = filteredIds.length > 0 && filteredIds.every(id => publishIds.has(id));
  const somePublishChk = filteredIds.some(id => publishIds.has(id));

  const toggleAllApprove = () => {
    setApproveIds(prev => {
      const next = new Set(prev);
      if (allApproveChk) approveEligible.forEach(id => next.delete(id));
      else approveEligible.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleAllPublish = () => {
    setPublishIds(prev => {
      const next = new Set(prev);
      if (allPublishChk) {
        filteredIds.forEach(id => next.delete(id));
      } else {
        filteredIds.forEach(id => {
          next.add(id);
          const j = allJobs.find(x => x.id === id);
          if (j?.approval_state === 'NEEDS_APPROVAL')
            setApproveIds(p => new Set([...p, id]));
        });
      }
      return next;
    });
  };

  // ── Summary ───────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const approveOnlyIds = [], approvePubIds = [], publishOnlyIds = [];
    publishIds.forEach(id => {
      const j = allJobs.find(x => x.id === id);
      if (!j) return;
      if (j.approval_state === 'NEEDS_APPROVAL') approvePubIds.push(id);
      else publishOnlyIds.push(id);
    });
    approveIds.forEach(id => { if (!publishIds.has(id)) approveOnlyIds.push(id); });

    const byId = id => allJobs.find(j => j.id === id);
    return {
      approveOnlyIds, approveOnlyJobs : approveOnlyIds.map(byId).filter(Boolean),
      approvePubIds,  approvePubJobs  : approvePubIds.map(byId).filter(Boolean),
      publishOnlyIds, publishOnlyJobs : publishOnlyIds.map(byId).filter(Boolean),
      totalActions: approveOnlyIds.length + approvePubIds.length + publishOnlyIds.length,
    };
  }, [approveIds, publishIds, allJobs]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/batch-schedule/jobs/approve-publish', {
        approve_job_ids:             summary.approveOnlyIds,
        approve_and_publish_job_ids: summary.approvePubIds,
        publish_job_ids:             summary.publishOnlyIds,
      });
      onActionComplete?.();
      onOpenChange(false);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || 'Action failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveFilter = filterText || filterState !== 'all' || filterBy !== 'all' ||
    filterDateFrom || filterDateTo || filterProvFrom || filterProvTo;

  const clearFilters = () => {
    setFilterText('');
    setFilterState('all');
    setFilterBy('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterProvFrom('');
    setFilterProvTo('');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex flex-col overflow-hidden"
        style={{ width: '80rem', maxWidth: '97vw', maxHeight: '94vh' }}
        data-testid="approve-publish-dialog"
      >
        <DialogHeader className="flex-shrink-0 pb-2 border-b">
          <DialogTitle className="text-lg font-semibold">Approve &amp; Publish</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {step === 'review'
              ? 'Review the breakdown below then confirm.'
              : 'Tick Approve to keep as internal draft. Tick Publish to release externally (auto-approves if needed).'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mr-2" />
            <span className="text-sm text-slate-500">Loading pending jobs…</span>
          </div>
        )}

        {!loading && loadError && (
          <div className="py-10 text-center text-sm text-destructive">{loadError}</div>
        )}

        {!loading && !loadError && allJobs.length === 0 && (
          <div className="py-14 text-center text-sm text-muted-foreground">
            No jobs are currently pending approval or publication.
          </div>
        )}

        {/* ── Browse Step ── */}
        {!loading && !loadError && allJobs.length > 0 && step === 'browse' && (
          <div className="flex flex-col flex-1 min-h-0 gap-2 pt-1">

            {/* Month pills */}
            <div className="flex flex-wrap gap-2 flex-shrink-0" data-testid="month-pills">
              {monthGroups.map(g => (
                <button
                  key={g.key}
                  onClick={() => setSelMonth(g.key)}
                  data-testid={`month-pill-${g.key}`}
                  className={[
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                    selMonth === g.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                  ].join(' ')}
                >
                  {g.label}
                  {g.needs > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${selMonth === g.key ? 'bg-amber-400/80 text-amber-900' : 'bg-amber-100 text-amber-700'}`}>
                      {g.needs}
                    </span>
                  )}
                  {g.publish > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${selMonth === g.key ? 'bg-emerald-400/80 text-emerald-900' : 'bg-emerald-100 text-emerald-700'}`}>
                      {g.publish}
                    </span>
                  )}
                </button>
              ))}
              <span className="ml-1 self-center text-[10px] text-slate-400">
                <span className="text-amber-600">■</span> needs approval &nbsp;
                <span className="text-emerald-600">■</span> needs publication
              </span>
            </div>

            {/* Filter row 1 — text, state, submitter */}
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  className="pl-8 h-8 text-sm w-52"
                  placeholder="Site, auditor or batch…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  data-testid="ap-filter-text"
                />
                {filterText && (
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setFilterText('')}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <select className="h-8 text-xs border rounded-md px-2 text-slate-700 bg-white"
                value={filterState} onChange={e => setFilterState(e.target.value)}
                data-testid="ap-filter-state">
                <option value="all">All states</option>
                <option value="needs_approval">Needs approval</option>
                <option value="approved_unpublished">Ready to publish</option>
              </select>

              {submitters.length > 0 && (
                <select className="h-8 text-xs border rounded-md px-2 text-slate-700 bg-white"
                  value={filterBy} onChange={e => setFilterBy(e.target.value)}
                  data-testid="ap-filter-by">
                  <option value="all">All requesters</option>
                  {submitters.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}

              {hasActiveFilter && (
                <button className="ml-1 text-xs text-slate-400 hover:text-slate-700 underline"
                  onClick={clearFilters} data-testid="ap-clear-filters">
                  Clear filters
                </button>
              )}

              {(approveIds.size > 0 || publishIds.size > 0) && (
                <button
                  className="ml-auto text-xs text-slate-500 hover:text-slate-800 underline"
                  onClick={() => { setApproveIds(new Set()); setPublishIds(new Set()); }}
                  data-testid="ap-clear-selection">
                  Clear all selections
                </button>
              )}
            </div>

            {/* Filter row 2 — date ranges */}
            <div className="flex flex-wrap items-center gap-3 flex-shrink-0 text-xs text-slate-600">
              <span className="text-slate-400 font-medium">Job date:</span>
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  className="h-7 text-xs border rounded px-2 text-slate-700"
                  value={filterDateFrom}
                  onChange={e => setFilterDateFrom(e.target.value)}
                  data-testid="ap-filter-date-from"
                />
                <span className="text-slate-400">–</span>
                <input
                  type="date"
                  className="h-7 text-xs border rounded px-2 text-slate-700"
                  value={filterDateTo}
                  onChange={e => setFilterDateTo(e.target.value)}
                  data-testid="ap-filter-date-to"
                />
              </div>

              <span className="text-slate-400 font-medium ml-2">Submitted / created:</span>
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  className="h-7 text-xs border rounded px-2 text-slate-700"
                  value={filterProvFrom}
                  onChange={e => setFilterProvFrom(e.target.value)}
                  data-testid="ap-filter-prov-from"
                />
                <span className="text-slate-400">–</span>
                <input
                  type="date"
                  className="h-7 text-xs border rounded px-2 text-slate-700"
                  value={filterProvTo}
                  onChange={e => setFilterProvTo(e.target.value)}
                  data-testid="ap-filter-prov-to"
                />
              </div>

              <span className="ml-2 text-slate-400">
                {filtered.length} of {monthJobs.length} row{monthJobs.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-md min-h-0" data-testid="ap-job-table">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-10" />{/* Approve */}
                  <col className="w-10" />{/* Publish */}
                  <col className="w-36" />{/* Date range */}
                  <col />{/* Site */}
                  <col />{/* Auditors */}
                  <col className="w-28" />{/* Requester */}
                  <col className="w-28" />{/* When */}
                  <col className="w-24" />{/* Status */}
                  <col className="w-28" />{/* Batch */}
                </colgroup>
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-center" title="Approve column">
                      <div className="flex flex-col items-center gap-0.5">
                        <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
                        <Checkbox
                          checked={allApproveChk}
                          data-state={someApproveChk && !allApproveChk ? 'indeterminate' : undefined}
                          onCheckedChange={toggleAllApprove}
                          data-testid="ap-select-all-approve"
                        />
                      </div>
                    </th>
                    <th className="px-2 py-2 text-center" title="Reject column — NEEDS_APPROVAL only">
                      <div className="flex flex-col items-center gap-0.5">
                        <XCircle className="h-3.5 w-3.5 text-rose-500" />
                        <span className="text-[9px] font-semibold uppercase tracking-wide text-rose-400">Reject</span>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-center" title="Publish column">
                      <div className="flex flex-col items-center gap-0.5">
                        <Globe className="h-3.5 w-3.5 text-emerald-600" />
                        <Checkbox
                          checked={allPublishChk}
                          data-state={somePublishChk && !allPublishChk ? 'indeterminate' : undefined}
                          onCheckedChange={toggleAllPublish}
                          data-testid="ap-select-all-publish"
                        />
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Date Range" col="date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Site" col="site" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Auditor(s)" col="auditors" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Requester" col="by" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Submission" col="when" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left">
                      <SortHeader label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Batch
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground text-sm">
                        No jobs match the current filter.
                      </td>
                    </tr>
                  )}
                  {filtered.map(job => {
                    const needsApproval = job.approval_state === 'NEEDS_APPROVAL';
                    const appChk = needsApproval ? approveIds.has(job.id) : true;
                    const rejChk = rejectIds.has(job.id);
                    const pubChk = publishIds.has(job.id);
                    const prov  = provenance(job);
                    const rowCls = rejChk
                      ? 'bg-rose-50 hover:bg-rose-50/80'
                      : pubChk
                        ? 'bg-emerald-50 hover:bg-emerald-50/80'
                        : appChk && needsApproval
                          ? 'bg-amber-50 hover:bg-amber-50/80'
                          : 'hover:bg-slate-50';

                    return (
                      <tr key={job.id} className={rowCls} data-testid={`ap-job-row-${job.id}`}>
                        {/* Approve tick */}
                        <td className="px-2 py-2.5 text-center">
                          {needsApproval ? (
                            <Checkbox
                              checked={appChk}
                              onCheckedChange={() => toggleApprove(job)}
                              data-testid={`ap-approve-${job.id}`}
                            />
                          ) : (
                            <Lock className="h-3.5 w-3.5 text-slate-300 mx-auto" title="Already approved" />
                          )}
                        </td>
                        {/* Reject tick — NEEDS_APPROVAL only; APPROVED_UNPUBLISHED & published ineligible */}
                        <td className="px-2 py-2.5 text-center">
                          {needsApproval ? (
                            <Checkbox
                              checked={rejChk}
                              onCheckedChange={() => toggleReject(job)}
                              className="data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                              data-testid={`ap-reject-${job.id}`}
                            />
                          ) : (
                            <Lock className="h-3.5 w-3.5 text-slate-200 mx-auto" title="Approved — ineligible for rejection" />
                          )}
                        </td>
                        {/* Publish tick */}
                        <td className="px-2 py-2.5 text-center">
                          <Checkbox
                            checked={pubChk}
                            onCheckedChange={() => togglePublish(job)}
                            data-testid={`ap-publish-${job.id}`}
                          />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                          {job.proposed_date ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-slate-900">{fmtDate(job.proposed_date)}</span>
                              <span className="text-[11px] text-slate-400 line-through">{fmtRange(job)}</span>
                              <span className="text-[10px] bg-violet-100 text-violet-700 border border-violet-200 px-1 py-0.5 rounded w-fit font-normal">date change</span>
                            </div>
                          ) : (
                            <span className="truncate">{fmtRange(job)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700 truncate">{job.site_name || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-600 truncate">
                          {(job.auditor_names || []).join(', ') || '—'}
                        </td>
                        {/* Requester: proposal submitter (if date change) or truthful provenance */}
                        <td className="px-3 py-2.5 truncate">
                          {job.proposal_submitted_by ? (
                            <span className="text-slate-700 text-xs">{job.proposal_submitted_by}</span>
                          ) : prov.isDraft ? (
                            <span className="text-slate-400 italic text-xs">{prov.by}</span>
                          ) : (
                            <span className="text-slate-700 text-xs">{prov.by}</span>
                          )}
                        </td>
                        {/* Submission: proposal time (if date change) or submitted_at / "Created [time]" for drafts */}
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                          {job.proposal_submitted_at ? (
                            <span className="text-slate-500">{fmtRel(job.proposal_submitted_at)}</span>
                          ) : prov.isDraft ? (
                            prov.when
                              ? <span className="text-slate-400 italic">Created {prov.when}</span>
                              : <span className="text-slate-300 italic">Not submitted</span>
                          ) : (
                            <span className="text-slate-500">{prov.when || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {needsApproval ? (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-normal text-[10px]">
                              {prov.isDraft
                                ? 'draft'
                                : job.batch_status === 'approved'
                                  ? 'needs re-approval'
                                  : (job.batch_status || 'pending').replace('_', ' ')}
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-300 font-normal text-[10px]">
                              approved
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-400 truncate">
                          {job.batch_name}
                          {job.is_bb_batch && (
                            <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 border-blue-300 text-blue-600">BB</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Selection summary bar */}
            {summary.totalActions > 0 && (
              <div className="flex-shrink-0 flex flex-wrap items-center gap-3 bg-slate-50 border rounded-md px-3 py-2 text-xs text-slate-700">
                {summary.approveOnlyIds.length > 0 && (
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
                    <strong>{summary.approveOnlyIds.length}</strong>&nbsp;approve-only
                  </span>
                )}
                {summary.approvePubIds.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-emerald-600" />
                    <strong>{summary.approvePubIds.length}</strong>&nbsp;approve &amp; publish
                  </span>
                )}
                {summary.publishOnlyIds.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-emerald-600" />
                    <strong>{summary.publishOnlyIds.length}</strong>&nbsp;publish-only
                  </span>
                )}
              </div>
            )}
            {/* Reject selection summary */}
            {rejectIds.size > 0 && (
              <div className="flex-shrink-0 flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-700">
                <XCircle className="h-3.5 w-3.5 text-rose-500 flex-shrink-0" />
                <strong>{rejectIds.size}</strong>&nbsp;selected for rejection
              </div>
            )}
          </div>
        )}

        {/* Inline reject reason modal overlay */}
        {showRejectModal && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-20 rounded-xl p-8">
            <div className="w-full max-w-md flex flex-col gap-3">
              <div className="flex items-center gap-2 text-rose-700 font-semibold">
                <XCircle className="h-5 w-5" />
                Reject {rejectIds.size} job{rejectIds.size !== 1 ? 's' : ''}
              </div>
              <p className="text-sm text-slate-600">
                Provide a reason. The requester will be notified (delivery subject to email configuration).
                Proposal rejections preserve the original job at its current date and approval state.
              </p>
              <Textarea
                placeholder="Reason for rejection (required)"
                value={rejectReason}
                onChange={e => { setRejectReason(e.target.value); setRejectError(''); }}
                rows={3}
                autoFocus
                data-testid="ap-reject-reason"
              />
              {rejectError && (
                <p className="text-sm text-rose-600" data-testid="ap-reject-error">{rejectError}</p>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowRejectModal(false); setRejectReason(''); setRejectError(''); }}
                  className="px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || rejecting}
                  className="px-4 py-2 text-sm rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
                  data-testid="ap-reject-confirm"
                >
                  {rejecting ? 'Rejecting…' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Review Step ── */}
        {step === 'review' && (
          <div className="flex flex-col gap-4 py-2 flex-1 overflow-auto">
            {summary.approveOnlyJobs.length > 0 && (
              <ReviewSection
                icon={<ShieldCheck className="h-4 w-4" />}
                colorCls="text-amber-700"
                title={`Approve only — ${summary.approveOnlyJobs.length} job${summary.approveOnlyJobs.length !== 1 ? 's' : ''} (internal draft, no notification)`}
                jobs={summary.approveOnlyJobs}
              />
            )}
            {summary.approvePubJobs.length > 0 && (
              <ReviewSection
                icon={<Globe className="h-4 w-4" />}
                colorCls="text-emerald-700"
                title={`Approve & Publish — ${summary.approvePubJobs.length} job${summary.approvePubJobs.length !== 1 ? 's' : ''} (external release + notification)`}
                jobs={summary.approvePubJobs}
              />
            )}
            {summary.publishOnlyJobs.length > 0 && (
              <ReviewSection
                icon={<Globe className="h-4 w-4" />}
                colorCls="text-emerald-700"
                title={`Publish only — ${summary.publishOnlyJobs.length} job${summary.publishOnlyJobs.length !== 1 ? 's' : ''} (already approved; external release + notification)`}
                jobs={summary.publishOnlyJobs}
              />
            )}
            {submitError && (
              <p className="text-sm text-destructive" data-testid="ap-submit-error">{submitError}</p>
            )}
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 border-t pt-3 gap-2">
          {step === 'browse' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ap-cancel-btn">Cancel</Button>
              {rejectIds.size > 0 && (
                <Button
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => setShowRejectModal(true)}
                  data-testid="ap-reject-selected-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject ({rejectIds.size})
                </Button>
              )}
              <Button
                disabled={summary.totalActions === 0}
                onClick={() => setStep('review')}
                data-testid="ap-review-btn"
              >
                Review ({summary.totalActions})
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setStep('browse'); setSubmitError(null); }}
                disabled={submitting} data-testid="ap-back-btn">
                <ChevronLeft className="h-4 w-4 mr-1" />Back
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleConfirm}
                disabled={submitting}
                data-testid="ap-confirm-btn"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Working…</>
                  : <><Check className="h-4 w-4 mr-1" />Confirm ({summary.totalActions})</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review section sub-component ─────────────────────────────────────────────

function ReviewSection({ icon, colorCls, title, jobs }) {
  return (
    <section>
      <h3 className={`text-sm font-semibold ${colorCls} flex items-center gap-1 mb-2`}>
        {icon}{title}
      </h3>
      <div className="border rounded-md divide-y overflow-auto max-h-48">
        {jobs.map(j => {
          const prov = provenance(j);
          return (
            <div key={j.id} className="flex items-start justify-between px-3 py-2 text-sm hover:bg-slate-50">
              <div>
                {j.proposed_date ? (
                  <span className="font-medium text-slate-800">
                    {fmtDate(j.proposed_date)}
                    <span className="line-through text-slate-400 text-xs ml-1">{fmtDate(j.scheduled_date)}</span>
                  </span>
                ) : (
                  <span className="font-medium text-slate-800">{fmtRange(j)}</span>
                )}
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-slate-600">{j.site_name || '—'}</span>
                {j.auditor_names?.length > 0 && (
                  <span className="text-slate-400 ml-1">({j.auditor_names.join(', ')})</span>
                )}
                {prov.isDraft && (
                  <span className="ml-2 text-[10px] text-amber-600 italic">draft</span>
                )}
                {j.proposed_date && (
                  <span className="ml-2 text-[10px] text-violet-600">date change</span>
                )}
              </div>
              <span className="text-xs text-slate-400 ml-3 whitespace-nowrap pt-0.5">{j.batch_name}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
